import React, { useEffect, useState } from 'react';
import { ConnectionStatus } from '../types/connections';
import { connectionService } from '../services/connectionService';

// Determine redirect URI based on environment
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || 
  (process.env.NODE_ENV === 'production' 
    ? `${window.location.origin}/oauth2callback`
    : "http://localhost:3001/oauth2callback");
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Google Ads requires its own dedicated GCP project/OAuth client (a developer
// token is permanently paired to the first GCP project it's used with), so
// Google Ads connections use a separate client ID from GBQ/GA4.
const GOOGLE_ADS_CLIENT_ID = process.env.REACT_APP_GOOGLE_ADS_CLIENT_ID || GOOGLE_CLIENT_ID;

const googleAuthUrl = (scope: string, clientId?: string) =>
  `https://accounts.google.com/o/oauth2/v2/auth?` +
  `client_id=${clientId || GOOGLE_CLIENT_ID}` +
  `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
  `&response_type=code` +
  `&scope=${encodeURIComponent(scope)}` +
  `&access_type=offline` +
  `&prompt=consent`;

const SCOPE_GBQ = 'https://www.googleapis.com/auth/bigquery openid email profile';
const SCOPE_GA4 = 'https://www.googleapis.com/auth/analytics.readonly openid email profile';
const SCOPE_GADS = 'https://www.googleapis.com/auth/adwords openid email profile';

// Функция для корректного отображения даты из Firestore
const toDateString = (ts: any) => {
  if (!ts) return '';
  if (typeof ts === 'string' || typeof ts === 'number') return new Date(ts).toLocaleDateString();
  if (typeof ts === 'object' && ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString();
  return '';
};

const ConnectionManager: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const connectionStatus = await connectionService.getStatus();
      setStatus(connectionStatus);
    } catch (err) {
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };



  const startLongLivedOAuth = (type: 'gbq' | 'ga4' | 'gads', connectionId?: string) => {
    const scope = type === 'gbq' ? SCOPE_GBQ : type === 'gads' ? SCOPE_GADS : SCOPE_GA4;
    const clientId = type === 'gads' ? GOOGLE_ADS_CLIENT_ID : GOOGLE_CLIENT_ID;
    const idToken = localStorage.getItem('id_token');
    const stateObj = { idToken, service: type, connectionId };
    const state = encodeURIComponent(btoa(JSON.stringify(stateObj)));
    const url = googleAuthUrl(scope, clientId) + `&state=${state}`;
    const width = 500, height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    window.open(
      url,
      'GoogleOAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    const listener = (event: MessageEvent) => {
      if (event.data === 'oauth-success') {
        window.removeEventListener('message', listener);
        loadStatus();
      }
    };
    window.addEventListener('message', listener);
  };

  const handleDisconnect = async (type: 'gbq' | 'ga4' | 'gads', id: string) => {
    try {
      console.log(`Attempting to disconnect ${type} connection with id: ${id}`);
      await connectionService.disconnect(type, id);
      console.log(`Successfully disconnected ${type} connection`);
      await loadStatus();
    } catch (err) {
      console.error(`Error disconnecting ${type}:`, err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to disconnect ${type}: ${errorMessage}`);
    }
  };

  const handleReconnect = (type: 'gbq' | 'ga4' | 'gads', id: string) => {
    // Start OAuth flow with connectionId to update existing connection
    startLongLivedOAuth(type, id);
  };



  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span>Loading connections...</span>
      </div>
    );
  }
  if (error) return <div className="error-alert">{error}</div>;

  return (
    <div className="connection-manager">
      <div className="connection-section">
        <h3>BigQuery Connections</h3>
        {status?.gbq && status.gbq.length > 0 ? (
          <div>
            {status.gbq.map((connection) => (
              <div key={connection.id} className="connection-item">
                <div className="connection-info">
                  <p>Email: {connection.email}</p>
                  <small>Connected: {toDateString(connection.lastUpdated)}</small>
                  {connection.reauthRequired && (
                    <small className="error-text">
                      Re-authentication required
                    </small>
                  )}
                </div>
                <div className="connection-actions">
                  {connection.reauthRequired && (
                    <button 
                      className="reconnect-button"
                      onClick={() => handleReconnect('gbq', connection.id)}
                    >
                      Reconnect
                    </button>
                  )}
                  <button 
                    className="delete-button"
                    onClick={() => handleDisconnect('gbq', connection.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button 
              className="add-connection-button"
              onClick={() => startLongLivedOAuth('gbq')}
            >
              Add new connect
            </button>
          </div>
        ) : (
          <div className="connection-empty-state">
            <div className="empty-state-icon">🔌</div>
            <p>No BigQuery connections</p>
            <button className="add-connection-button" onClick={() => startLongLivedOAuth('gbq')}>
              Add new connection
            </button>
          </div>
        )}
      </div>
      <div className="connection-section">
        <h3>GA4 Connections</h3>
        {status?.ga4 && status.ga4.length > 0 ? (
          <div>
            {status.ga4.map((connection) => (
              <div key={connection.id} className="connection-item">
                <div className="connection-info">
                  <p>Email: {connection.email}</p>
                  <small>Property: {connection.propertyId}</small>
                  <small>Connected: {toDateString(connection.lastUpdated)}</small>
                  {connection.reauthRequired && (
                    <small className="error-text">
                      Re-authentication required
                    </small>
                  )}
                </div>
                <div className="connection-actions">
                  {connection.reauthRequired && (
                    <button 
                      className="reconnect-button"
                      onClick={() => handleReconnect('ga4', connection.id)}
                    >
                      Reconnect
                    </button>
                  )}
                  <button 
                    className="delete-button"
                    onClick={() => handleDisconnect('ga4', connection.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button 
              className="add-connection-button"
              onClick={() => startLongLivedOAuth('ga4')}
            >
              Add new connection
            </button>
          </div>
        ) : (
          <div className="connection-empty-state">
            <div className="empty-state-icon">📊</div>
            <p>No GA4 connections</p>
            <button className="add-connection-button" onClick={() => startLongLivedOAuth('ga4')}>
              Add new connection
            </button>
          </div>
        )}
      </div>
      <div className="connection-section">
        <h3>Google Ads Connections</h3>
        {status?.gads && status.gads.length > 0 ? (
          <div>
            {status.gads.map((connection) => (
              <div key={connection.id} className="connection-item">
                <div className="connection-info">
                  <p>Email: {connection.email}</p>
                  <small>Connected: {toDateString(connection.lastUpdated)}</small>
                  {connection.reauthRequired && (
                    <small className="error-text">
                      Re-authentication required
                    </small>
                  )}
                </div>
                <div className="connection-actions">
                  {connection.reauthRequired && (
                    <button 
                      className="reconnect-button"
                      onClick={() => handleReconnect('gads', connection.id)}
                    >
                      Reconnect
                    </button>
                  )}
                  <button 
                    className="delete-button"
                    onClick={() => handleDisconnect('gads', connection.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button 
              className="add-connection-button"
              onClick={() => startLongLivedOAuth('gads')}
            >
              Add new connection
            </button>
          </div>
        ) : (
          <div className="connection-empty-state">
            <div className="empty-state-icon">📢</div>
            <p>No Google Ads connections</p>
            <button className="add-connection-button" onClick={() => startLongLivedOAuth('gads')}>
              Add new connection
            </button>
          </div>
        )}
      </div>


    </div>
  );
};

export default ConnectionManager; 